import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { Customized, Scatter, XAxis } from 'recharts'
import useGetVsic from '../../../../../../common/category/useGetVsic'
import { ChartContainer } from '../../../../../../common/chart/chartContainer'
import { Legend } from '../../../../../../common/chart/legend'
import { Element } from '../../../../../../common/html/element'
import { formatByTheme } from '../../../../../../helpers/theme'
import { formatVal } from '../../../../../../helpers/value'
import { selectUserType } from '../../../../../../layout/store/slice'
import { USER_TYPE } from '../../../../../../utils/authorization'
import {
  selectCompareIndustry,
  selectDefaultIndustry,
} from '../../../store/slice'
import { CustomXAxisTick } from '../../common/CustomXAxisTick'
import { KEY_DATA, MAP_TYPE_TITLE } from '../../constant'
import { selectCompareVal, selectData } from '../../store/slice'

const RATIO_CONFIG = [
  {
    titleKey: 'companyAnalysis.industryAnalysis.NET_REVENUE_GROWTH',
    dataKey: KEY_DATA.NET_REVENUE_GROWTH,
    legendKey: KEY_DATA.NET_REVENUE_GROWTH,
  },
  {
    titleKey: 'companyAnalysis.industryAnalysis.NET_PROFIT_MARGIN',
    dataKey: KEY_DATA.NET_PROFIT_MARGIN,
    legendKey: KEY_DATA.NET_PROFIT_MARGIN,
  },
  {
    titleKey: 'companyAnalysis.industryAnalysis.ROA',
    dataKey: KEY_DATA.ROA,
    legendKey: KEY_DATA.ROA,
  },
  {
    titleKey: 'companyAnalysis.industryAnalysis.TOTAL_DEBT_TO_TOTAL_EQUITY',
    dataKey: KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY,
    legendKey: KEY_DATA.TOTAL_DEBT_TO_TOTAL_EQUITY,
  },
  {
    titleKey: 'companyAnalysis.industryAnalysis.CURRENT_RATIO',
    dataKey: KEY_DATA.CURRENT_RATIO,
    legendKey: KEY_DATA.CURRENT_RATIO,
  },
]

const ChartRatioComparison = ({ isDynamicReport = false }) => {
  const data = useSelector(selectData)
  const locale = useSelector((state) => state.i18n.locale)
  const { vsics } = useGetVsic()
  const vsicCompare = useSelector(selectCompareIndustry)
  const vsicDefault = useSelector(selectDefaultIndustry)
  const userType = useSelector(selectUserType)
  const compareVal = useSelector(selectCompareVal)
  const defaultData = data[compareVal]?.defaultIndustry?.ratio
  const comparingData = data[compareVal]?.comparingIndustry?.ratio

  const titleDefaultIndustry = vsics.find(
    (item) => item.id === vsicDefault,
  )?.name
  const titleComparingIndustry =
    vsicDefault === vsicCompare
      ? titleDefaultIndustry + '(1)'
      : vsics.find((item) => item.id === vsicCompare)?.name

  const [checked, setChecked] = useState(Object.values(KEY_DATA))

  const chartData = useMemo(() => {
    const buildGroup = (groupLabel, srcData) =>
      RATIO_CONFIG.filter((item) => checked.includes(item.dataKey)).map(
        (item) => ({
          group: groupLabel,
          title: I18n.t(item.titleKey),
          [KEY_DATA.VALUE_1]: srcData?.[0]?.[item.dataKey],
          [KEY_DATA.VALUE_2]: srcData?.[1]?.[item.dataKey],
          legendKey: item.legendKey,
        }),
      )

    let result = buildGroup(titleDefaultIndustry, defaultData)

    if (vsicCompare) {
      result = result.concat(buildGroup(titleComparingIndustry, comparingData))
    }

    return result
  }, [data, vsicCompare, checked])

  const legendSector = [
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1],
      dataKey: KEY_DATA.VALUE_1,
      color: '#006AA6 ',
      hide: !checked.includes(KEY_DATA.VALUE_1),
    },
    {
      title: MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2],
      dataKey: KEY_DATA.VALUE_2,
      color: '#FFCD60',
      hide: !checked.includes(KEY_DATA.VALUE_2),
    },
  ]

  const groups = [titleDefaultIndustry, titleComparingIndustry]
  const legendRatio = RATIO_CONFIG.map((item) => ({
    title: item.titleKey,
    dataKey: item.legendKey,
    color: '#F49966',
  }))

  return (
    <div className="dFlex gap4">
      <div className="w80">
        <ChartContainer
          data={chartData}
          width="100%"
          height={350}
          margin={{
            top: 20,
            right: 30,
            left: 0,
            bottom: 60,
          }}
          yAxis={[
            {
              yAxisId: 'left',
              dataKeys: [KEY_DATA.VALUE_1, KEY_DATA.VALUE_2],
              isLineChart: true,
            },
          ]}
          tooltip={{
            schema: {
              content: [
                {
                  dataKey: KEY_DATA.VALUE_1,
                  label: I18n.t(MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_1]),
                  formatValue: (value) => formatVal(value, locale),
                },
                {
                  dataKey: KEY_DATA.VALUE_2,
                  label: I18n.t(MAP_TYPE_TITLE[compareVal][KEY_DATA.VALUE_2]),
                  formatValue: (value) => formatVal(value, locale),
                },
              ],
            },
          }}
          xAxis={[
            {
              dataKey: 'title',
              interval: 0,
              height: 40,
              padding: { left: 80, right: 80 },
            },
          ]}
        >
          <XAxis
            xAxisId="group"
            dataKey="group"
            axisLine={false}
            tickLine={false}
            height={30}
            interval={0}
            ticks={groups}
            tick={
              <CustomXAxisTick
                titleDefaultIndustry={titleDefaultIndustry}
                isCompare={!!vsicCompare}
                isDynamicReport={isDynamicReport}
              />
            }
          />
          {checked.includes(KEY_DATA.VALUE_1) &&
            checked.includes(KEY_DATA.VALUE_2) && (
              <Customized
                component={(props) => {
                  const { formattedGraphicalItems } = props
                  const lines = []
                  if (
                    !formattedGraphicalItems ||
                    formattedGraphicalItems.length < 2
                  )
                    return null
                  const value1Points =
                    formattedGraphicalItems[0]?.props?.points || []
                  const value2Points =
                    formattedGraphicalItems[1]?.props?.points || []

                  for (let i = 0; i < value1Points.length; i++) {
                    const p1 = value1Points[i]
                    const p2 = value2Points[i]
                    if (p1.value1 && p1.value2) {
                      lines.push(
                        <line
                          key={i}
                          x1={p1.cx}
                          y1={p1.cy}
                          x2={p2.cx}
                          y2={p2.cy}
                          stroke={formatByTheme('#9C9C9C')}
                          strokeWidth={2}
                        />,
                      )
                    }
                  }
                  return <g>{lines}</g>
                }}
              />
            )}
          {legendSector.map((item) => (
            <Scatter
              key={item.dataKey}
              hide={item.hide}
              yAxisId="left"
              dataKey={item.dataKey}
              fill={item.color}
              isAnimationActive={false}
              shape={(props) => {
                if (isNaN(props[item.dataKey])) return null
                return (
                  <circle cx={props.cx} cy={props.cy} r={6} fill={props.fill} />
                )
              }}
            />
          ))}
        </ChartContainer>
      </div>
      <div className="w20">
        <Element className="fw600 mt5" style={{ color: '#191d25' }}>
          <Translate value={'companyAnalysis.industryAnalysis.SECTOR'} />
        </Element>
        <Legend
          items={legendSector.map((item) => ({
            ...item,
            isCheckbox: userType?.userTypeId === USER_TYPE.PREMIUM,
            listCheckBox: checked,
            setListCheckBox: setChecked,
          }))}
          className="flexColumn mt1 mb2"
        />
        <Element className="fw600" style={{ color: '#191d25' }}>
          <Translate value={'companyAnalysis.industryAnalysis.RATIO_2'} />
        </Element>
        <Legend
          items={legendRatio.map((item) => ({
            ...item,
            isCheckbox: userType?.userTypeId === USER_TYPE.PREMIUM,
            listCheckBox: checked,
            setListCheckBox: setChecked,
          }))}
          className="flexColumn mt1"
        />
      </div>
    </div>
  )
}

export default ChartRatioComparison
